import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { KYC_DOCUMENTS_TYPES } from '@shinetools/kyc-validation';

import { OutstandingAgreementType, PackageType, Role } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import SuspenseQuery from 'components/SuspenseQuery';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import DownloadShineMessageSent from 'features/Onboarding/legacy/Pages/DownloadShineMessageSent';
import CapitalDepositNextSteps from 'features/Onboarding/pages/CapitalDepositNextSteps';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import OnboardingPageAdapter from '../../components/OnboardingPageAdapter';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import PricingPlan from '../../modules/PricingPlan';
import Verification from '../../modules/Verification';
import WelcomeInvitee from '../../pages/WelcomeInvitee';
import { InvitedAccountHolderCapitalDepositDocument } from './graphql/queries/invitedAccountHolderCapitalDeposit.gql';
import machineConfig, { model, State } from './machine';
import getNavigationSteps from './navigation/getNavigationSteps';

const InvitedAccountHolderCapitalDepositComponent = withStateMachine<
  typeof model
>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.data.viewer.profile as unknown as User,
    };

    let pageContent = null;

    switch (stringifiedState) {
      case State.INIT:
        pageContent = <OBLoaderPage onPrev={() => null} />;
        break;

      case State.WELCOME:
        pageContent = (
          <WelcomeInvitee
            companyProfile={moduleData.data.viewer.company.profile}
            isAccountHolder={true}
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
            profile={moduleData.data.viewer.profile}
          />
        );
        break;

      case State.VERIFICATION:
        pageContent = (
          <Verification
            applicantType={ApplicantType.InvitedAccountHolder}
            applicationType={ApplicationType.CapitalDeposit}
            companyProfileId={moduleData.data.viewer.company.companyProfileId}
            isMainApplicant={false}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : onPrev()
            }
          />
        );
        break;

      case State.CAPITAL_DEPOSIT_AGREEMENT:
        pageContent = (
          <Agreement
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : onPrev()
            }
            type={OutstandingAgreementType.NotaryFourez}
          />
        );
        break;

      case State.AGREEMENT:
        pageContent = (
          <Agreement
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : onPrev()
            }
            type={OutstandingAgreementType.Cgu}
          />
        );
        break;

      case State.CAPITAL_DEPOSIT_PLAN_CHOICE: {
        return (
          <PricingPlan
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? model.events.NEXT()
                : onPrev()
            }
            packageType={PackageType.CapitalDeposit}
          />
        );
      }

      case State.SEND_APPLICATION:
        pageContent = <OBLoaderPage onPrev={() => null} />;
        break;

      case State.CAPITAL_DEPOSIT_NEXT_STEPS:
        pageContent = (
          <CapitalDepositNextSteps
            goToBeneficiaries={() => send('GO_BENEFICIARIES_DOCS')}
            goToSmsSent={() => send('DOWNLOAD_SHINE')}
            onPrev={onPrev}
          />
        );
        break;

      case State.BENEFICIARIES_DOCS:
        pageContent = (
          <OnboardingPageAdapter
            {...adaptedPageProps}
            LegacyOnboardingPage={BeneficiariesDocs}
          />
        );
        break;

      case State.DOWNLOAD_SHINE_MESSAGE_SENT:
        pageContent = (
          <OnboardingPageAdapter
            {...adaptedPageProps}
            LegacyOnboardingPage={DownloadShineMessageSent}
          />
        );
        break;

      default:
        pageContent = assertNever(stringifiedState);
        break;
    }

    return (
      <NavigationContext.Provider
        value={{
          steps: getNavigationSteps(stringifiedState),
        }}
      >
        {pageContent}
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

const InvitedAccountHolderCapitalDeposit = asBentoModule<{
  companyProfileId: string;
}>(({ companyProfileId, onDone }) => {
  const query = useQuery(InvitedAccountHolderCapitalDepositDocument, {
    variables: {
      companyProfileId,
      kycDocumentTypes: KYC_DOCUMENTS_TYPES,
    },
  });

  return (
    <SuspenseQuery loader={<OBLoaderPage onPrev={() => null} />} query={query}>
      {(data) => {
        if (
          !data.viewer.companyUser.roles.includes(Role.AccountHolder) ||
          !data.viewer.company.isCreatedByMandatee ||
          !data.viewer.company.isCapitalDeposit
        ) {
          throw new Error(
            'InvitedAccountHolderCapitalDeposit module can only be accessed by account holders of capital deposit companies created by a mandatee',
          );
        }

        return (
          <InvitedAccountHolderCapitalDepositComponent
            moduleData={{ data }}
            onDone={onDone}
          />
        );
      }}
    </SuspenseQuery>
  );
});

export default InvitedAccountHolderCapitalDeposit;
